/**
 * externalLink — the one-way seam that lets any screen (deep in the tree, with
 * no access to the ActivityBridge) send the player to a URL OUTSIDE the game.
 *
 * Sibling of `discord/invite.ts`, same shape and same rules. The real capability
 * is `sdk.commands.openExternalLink({ url })` from @discord/embedded-app-sdk
 * (2.5.0): Discord shows its own "you're leaving Discord" confirmation and then
 * opens the link. It is the ONLY way out of the Activity iframe — a plain
 * `target="_blank"` anchor is sandboxed away inside Discord — so every embedded
 * caller must go through here.
 *
 * Activity.tsx registers the opener once the bridge is up; plain-browser
 * surfaces (`/`, `/play`, `?discordmock=1`) never register one, so
 * `externalLinkAvailable()` is false and callers keep their native anchor
 * behaviour instead.
 */

type ExternalLinkOpener = (url: string) => Promise<unknown>;

let opener: ExternalLinkOpener | null = null;

/** Register (or clear, with `null`) the Discord external-link opener. */
export function registerExternalLinkOpener(fn: ExternalLinkOpener | null): void {
  opener = fn;
}

/** True when Discord can be asked to open an external URL for us. */
export function externalLinkAvailable(): boolean {
  return opener != null;
}

/**
 * Ask Discord to open `url`. Resolves `true` only when Discord accepted the
 * command; `false` for "no opener registered" and for every rejection (RPC
 * timeout, host refusal). Never throws.
 *
 * NOTE a `true` return does NOT mean the player went: Discord's confirmation
 * modal may be declined, which still resolves. That is deliberate — a declined
 * modal must not make the caller ALSO pop a browser tab.
 */
export async function tryOpenExternalLink(url: string): Promise<boolean> {
  if (!opener) return false;
  try {
    await opener(url);
    return true;
  } catch {
    return false;
  }
}
